/**
 * Utility functions for timezone-safe date formatting and filter presentations in Cuadro Picnic.
 */

/**
 * Formats a given Date instance or ISO date string to standard YYYY-MM-DD format using local time components.
 * Avoids UTC timezone conversion shifts.
 *
 * @param targetDateValue - Target date object, ISO date string, or null/undefined.
 * @returns Localized YYYY-MM-DD date string, or empty string if input is null/undefined.
 */
export function formatLocalDateToYMD(
  targetDateValue: Date | string | null | undefined,
): string {
  if (!targetDateValue) {
    return '';
  }
  if (typeof targetDateValue === 'string') {
    const trimmedString = targetDateValue.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedString)) {
      return trimmedString;
    }
    const parsedDate = new Date(trimmedString);
    if (isNaN(parsedDate.getTime())) {
      return '';
    }
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  if (targetDateValue instanceof Date && !isNaN(targetDateValue.getTime())) {
    const year = targetDateValue.getFullYear();
    const month = String(targetDateValue.getMonth() + 1).padStart(2, '0');
    const day = String(targetDateValue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return '';
}

/**
 * Formats a Date instance or date string to presentation format DD/MM/YYYY.
 * Uses strict regular expressions on string inputs to prevent browser timezone offsets.
 *
 * @param targetDateValue - Date instance or string representing date.
 * @returns Formatted string as DD/MM/YYYY, or '-' if null/empty.
 */
export function formatLocalDateToDisplay(
  targetDateValue: Date | string | null | undefined,
): string {
  if (!targetDateValue) {
    return '-';
  }
  if (typeof targetDateValue === 'string') {
    const trimmedDateString = targetDateValue.trim();
    const ymdMatch = trimmedDateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymdMatch) {
      const [, yearPart, monthPart, dayPart] = ymdMatch;
      return `${dayPart}/${monthPart}/${yearPart}`;
    }
    const dmyMatch = trimmedDateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (dmyMatch) {
      const [, dayPart, monthPart, yearPart] = dmyMatch;
      return `${dayPart.padStart(2, '0')}/${monthPart.padStart(2, '0')}/${yearPart}`;
    }
  }
  const ymdString = formatLocalDateToYMD(targetDateValue);
  if (!ymdString) {
    return '-';
  }
  const [yearPart, monthPart, dayPart] = ymdString.split('-');
  return `${dayPart}/${monthPart}/${yearPart}`;
}

/**
 * Generates human-readable label for date range filter chips.
 *
 * @param startDate - Selected start date.
 * @param endDate - Selected end date.
 * @returns Human-readable range label (e.g. '01/09/2026 a 15/09/2026', 'Desde 01/09/2026', 'Hasta 15/09/2026').
 */
export function formatDateRangeFilterLabel(
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
): string {
  const displayStart = formatLocalDateToDisplay(startDate);
  const displayEnd = formatLocalDateToDisplay(endDate);

  if (startDate && endDate) {
    return `${displayStart} a ${displayEnd}`;
  }
  if (startDate) {
    return `Desde ${displayStart}`;
  }
  if (endDate) {
    return `Hasta ${displayEnd}`;
  }
  return '';
}
